import { getApiBaseUrl } from "../config/runtimeConfig";
import { invalidateLookup } from "../lookups/lookupsApi";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export interface Client {
  id: number;
  name: string;
  taxId?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  website?: string | null;
  address?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
  notes?: string | null;
  primaryContactName?: string | null;
  primaryContactRole?: string | null;
  primaryContactEmail?: string | null;
  primaryContactPhone?: string | null;
  paymentTerms?: string | null;
  tags: string[];
  workTypes: WorkType[];
  logoUrl?: string | null;
  activeProjectsCount: number;
  lastInteraction?: string | null;
  created: string;
  lastModified: string;
}

export interface WorkType {
  id: number;
  name: string;
  sortOrder: number;
  templateLineCount?: number;
}

export interface WorkTypePayload {
  name: string;
  sortOrder?: number;
}

export interface ClientPayload {
  name: string;
  taxId?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  website?: string | null;
  address?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
  notes?: string | null;
  primaryContactName?: string | null;
  primaryContactRole?: string | null;
  primaryContactEmail?: string | null;
  primaryContactPhone?: string | null;
  paymentTerms?: string | null;
  tags?: string[];
  workTypeIds?: number[];
  activeProjectsCount?: number;
  lastInteraction?: string | null;
}

export interface ClientLogoResponse {
  clientId: number;
  logoUrl?: string | null;
}

async function request<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(
      response.status,
      text || response.statusText,
    );
  }

  return (await response.json()) as T;
}

export function getClients(): Promise<Client[]> {
  return request<Client[]>("/api/clients");
}

export function getWorkTypes(): Promise<WorkType[]> {
  return request<WorkType[]>("/api/work-types");
}

export function createWorkType(
  payload: WorkTypePayload,
): Promise<WorkType> {
  return request<WorkType>("/api/work-types", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateWorkType(
  workTypeId: number,
  payload: WorkTypePayload,
): Promise<WorkType> {
  return request<WorkType>(`/api/work-types/${workTypeId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function deleteWorkType(workTypeId: number): Promise<void> {
  return request<void>(`/api/work-types/${workTypeId}`, {
    method: "DELETE",
  });
}

export function getClientById(clientId: number): Promise<Client> {
  return request<Client>(`/api/clients/${clientId}`);
}

export function createClient(payload: ClientPayload): Promise<Client> {
  return request<Client>("/api/clients", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  }).then((result) => {
    invalidateLookup("clients");
    return result;
  });
}

export function updateClient(
  clientId: number,
  payload: ClientPayload,
): Promise<Client> {
  return request<Client>(`/api/clients/${clientId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  }).then((result) => {
    invalidateLookup("clients");
    return result;
  });
}

export function uploadClientLogo(
  clientId: number,
  file: File,
): Promise<ClientLogoResponse> {
  const formData = new FormData();
  formData.append("logo", file, file.name || "logo");

  return request<ClientLogoResponse>(`/api/clients/${clientId}/logo`, {
    method: "POST",
    body: formData,
  });
}
